import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiLinkModel, FavoritesApiElementContentModel } from '@/services/platform/api/model';
import { cn, ModuleProperties, useClientTranslation } from '@/lib/core';
import { LinkRouteUrl } from '@/lib/core/utils';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useApp } from '@/lib/core/providers/appProvider';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { useFavoriteLists, useAddFavoriteItem } from '@/services/platform/data/shop';
import { useManageFavoriteList } from '@/services/platform/data/favorites';
import { CreateNewListSheet } from '@/components/shared/profile/favorites/sheets/CreateNewListSheet';
import { FavoritesActionToast } from '@/components/shared/profile/favorites/ui/FavoritesActionToast';
import { ProfileFavorites, FavoritesTab } from '@/components/shared/profile/favorites';
import { FavoritesListDetail } from '@/components/shared/profile/favorites/list-detail';

// ── Component ─────────────────────────────────────────────────────────────────

export const FavoritesApi = (props: ModuleProperties) => {
  const content = props.content as FavoritesApiElementContentModel | undefined;
  const navigate = useNavigate();
  const { appSettings } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const listParam = searchParams.get('list');
  const t = useClientTranslation();
  const [activeTab, setActiveTab] = useState<FavoritesTab>('Shop');
  const [isCreateListOpen, setCreateListOpen] = useState(false);
  // null = hidden; a string both shows the toast and supplies its message.
  const [actionToastMessage, setActionToastMessage] = useState<string | null>(null);
  const [selectedListGuid, setSelectedListGuid] = useState<string | undefined>();
  const { data: lists, isLoading, refetch } = useFavoriteLists();
  const { createList } = useAddFavoriteItem();
  const { renameList, deleteList } = useManageFavoriteList();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  // Deep-link from elsewhere in the app (e.g. the shop "added to list" toast) — opens the
  // matching list's detail view directly once its data has loaded. Guarded by a ref (not
  // `selectedListGuid` state) so it fires at most once per mount: clearing the selection to
  // go back updates React state and the URL's `list` param separately, and the URL update
  // lands a render later — a state-only guard would see the still-stale param and reopen it.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current || !listParam || lists.length === 0) return;
    hasAutoOpenedRef.current = true;
    const match = lists.find(l => l.publicGuid === listParam);
    if (!match) return;
    if (match.listType === 'Shop' || match.listType === 'Events') {
      setActiveTab(match.listType);
    }
    setSelectedListGuid(match.publicGuid);
  }, [listParam, lists]);

  const listsForTab = useMemo(() => lists.filter(l => l.listType === activeTab), [lists, activeTab]);
  const selectedList = listsForTab.find(l => l.publicGuid === selectedListGuid);

  // Closes the detail overlay back to the list overview. Also drops the `list` deep-link
  // param — otherwise the auto-open effect above would immediately reopen the same list.
  function closeListDetail() {
    setSelectedListGuid(undefined);
    if (listParam) {
      setSearchParams(
        params => {
          params.delete('list');
          return params;
        },
        { replace: true }
      );
    }
  }

  if (!content) return null;

  function buildExploreCta(
    link: ApiLinkModel | undefined
  ): { label: string; onClick: () => void } | undefined {
    const path = link?.route?.path ?? link?.url ?? undefined;
    if (!link?.title || !path) return undefined;
    return { label: link.title, onClick: () => navigate(path) };
  }

  const exploreCta =
    activeTab === 'Shop'
      ? buildExploreCta(content.properties?.shopListsCta?.[0])
      : buildExploreCta(content.properties?.eventListsCta?.[0]);

  async function handleCreateList(name: string) {
    const created = await createList(name, activeTab);
    if (created) {
      await refetch();
      setCreateListOpen(false);
      setActionToastMessage(
        activeTab === 'Events'
          ? t('App.Shop.Favorites.EventsListCreated')
          : t('App.Shop.Favorites.ShopListCreated')
      );
    }
  }

  async function handleEditName(name: string) {
    if (!selectedListGuid) return;
    const updated = await renameList(selectedListGuid, name);
    if (!updated) throw new Error('Failed to rename list');
    await refetch();
    setActionToastMessage(t('App.Shop.Favorites.ListNameUpdated'));
  }

  async function handleDeleteList() {
    if (!selectedListGuid) return;
    const deleted = await deleteList(selectedListGuid);
    if (!deleted) throw new Error('Failed to delete list');
    await refetch();
    setActionToastMessage(t('App.Shop.Favorites.DeleteListSuccess'));
  }

  function handleProductClick(publicGuid: string) {
    const shopUrl = LinkRouteUrl(appSettings?.properties?.shop?.route);
    navigate(`${shopUrl}/product_${publicGuid}`);
  }

  function handleEventClick(eventId: string) {
    const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;
    if (eventDetailPath) navigate(`${eventDetailPath}/${eventId}`);
  }

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__favorites-api']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      {/* Shop / Events tab switcher + list overview */}
      <ProfileFavorites
        activeTab={activeTab}
        onTabChange={setActiveTab}
        lists={listsForTab}
        isLoading={isLoading}
        onBack={() => navigate(-1)}
        onCreateList={() => setCreateListOpen(true)}
        onOpenList={setSelectedListGuid}
      />

      <CreateNewListSheet
        open={isCreateListOpen}
        onOpenChange={setCreateListOpen}
        listType={activeTab}
        onCreate={handleCreateList}
      />

      {/* Rising confirmation toast — shared by create / rename / delete */}
      <FavoritesActionToast
        open={actionToastMessage !== null}
        message={actionToastMessage ?? ''}
        onDismiss={() => setActionToastMessage(null)}
        anchor="bottom"
      />

      {/* List detail overlay — covers the whole screen above the CMS page header */}
      {selectedList && (
        <div className="bg-jncquoi-neutral-00 fixed inset-0 z-80">
          <FavoritesListDetail
            list={selectedList}
            activeTab={activeTab}
            onBack={closeListDetail}
            onEditName={handleEditName}
            onDeleteList={handleDeleteList}
            exploreCta={exploreCta}
            onProductClick={handleProductClick}
            onEventClick={handleEventClick}
            onItemsChanged={refetch}
          />
        </div>
      )}
    </ModuleWrapper>
  );
};
